import AttentionFilled from 'assets/icons/AttentionFilled';
import Error from 'assets/icons/Error';
import useTranslation from 'common/hooks/useTranslation';
import { selectCurrentCompany, selectUser } from 'common/slices/auth.slice';
import { formatDate } from 'common/utils';
import { isNull } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

const AI_STATUS_WARNING_ICON_COLORS = {
  error: '#BB0202',
  awaiting: '#F16347',
  outdated: '#084D6E',
};

const AI_STATUS_WARNING_ICONS = {
  error: (
    <div className='bg-white rounded-full'>
      <Error size='12' color={AI_STATUS_WARNING_ICON_COLORS.error} />
    </div>
  ),
  awaiting: (
    <AttentionFilled size='12' color={AI_STATUS_WARNING_ICON_COLORS.awaiting} />
  ),
  outdated: (
    <AttentionFilled size='12' color={AI_STATUS_WARNING_ICON_COLORS.outdated} />
  ),
};

export const useAIStatusWarning = (offer, submission) => {
  const { t, i18n } = useTranslation('offers\\details');
  const currentCompany = useSelector(selectCurrentCompany);
  const currentUser = useSelector(selectUser);
  const hasAccessToViewAiWarnings =
    currentUser?.role?.name === 'staff' || currentUser?.role?.name === 'admin';
  const activeIntegration = currentCompany?.active_integration;
  const aiLastSync =
    submission?.ai_info?.ai_last_sync ?? submission?.ai_last_sync ?? null;
  const aiInstructionsUpdatedAt = offer?.ai_instructions_updated_at ?? null;
  const aiUpdatedExternallyAt = submission?.ai_updated_externally_at ?? null;
  const errorsFromAPI = submission?.external_source_ai_error ?? null;
  const aiPushErrorsArr = Array.isArray(errorsFromAPI)
    ? errorsFromAPI
    : errorsFromAPI
    ? [errorsFromAPI]
    : [];

  const hasAiPushErrors = aiPushErrorsArr.length > 0;
  const isActiveIntegrationSAPSF = activeIntegration === 'SAPSF';
  const aiUpdatedExternallyAtFormatted = aiUpdatedExternallyAt
    ? formatDate(aiUpdatedExternallyAt, i18n, 'long', true)
    : null;

  const isAiOutdated = () => {
    if (aiInstructionsUpdatedAt && aiLastSync) {
      return new Date(aiInstructionsUpdatedAt) > new Date(aiLastSync);
    }
    return false;
  };

  const isAiOutdatedFlag = isAiOutdated();

  const isAiSyncExternallyOutdated = () => {
    if (!aiUpdatedExternallyAt || !aiLastSync) return false;
    return (
      new Date(aiUpdatedExternallyAt).getTime() < new Date(aiLastSync).getTime()
    );
  };

  const hasExternalUpdate = !isNull(aiUpdatedExternallyAt);
  const isAiSyncExternallyOutdatedFlag =
    hasExternalUpdate && isAiSyncExternallyOutdated();
  const isAiSyncExternallyMissing = !hasExternalUpdate && !isNull(aiLastSync);

  const aiStatusWarnings = [];

  if (isAiOutdatedFlag) {
    aiStatusWarnings.push(t('tooltip.not_synced'));
  }

  if (hasAiPushErrors && isActiveIntegrationSAPSF) {
    aiStatusWarnings.push(t('tooltip.ats-ai-push-failed'));
  }

  if (isAiSyncExternallyMissing && isActiveIntegrationSAPSF) {
    aiStatusWarnings.push(t('tooltip.ats-ai-push-awaiting-first-attempt'));
  }

  if (isAiSyncExternallyOutdatedFlag && isActiveIntegrationSAPSF) {
    aiStatusWarnings.push(t('tooltip.ats-ai-data-outdated'));
  }

  const showAIStatusWarning =
    aiStatusWarnings.length > 0 && hasAccessToViewAiWarnings;

  const aiStatusWarningTooltipContent = (
    <div className='flex flex-col gap-0.5'>
      {aiStatusWarnings.map((msg, idx) => (
        <div key={idx}>{msg}</div>
      ))}
    </div>
  );

  const aiStatusWarningIcon = () => {
    if (hasAiPushErrors) {
      return AI_STATUS_WARNING_ICONS.error;
    }

    if (isAiSyncExternallyMissing) {
      return AI_STATUS_WARNING_ICONS.awaiting;
    }

    if (isAiSyncExternallyOutdatedFlag || isAiOutdatedFlag) {
      return AI_STATUS_WARNING_ICONS.outdated;
    }

    return null;
  };

  const aiStatusBannerType = () => {
    if (hasAiPushErrors) {
      return 'error';
    }
    if (isAiSyncExternallyMissing) {
      return 'awaiting';
    }
    if (isAiSyncExternallyOutdatedFlag || isAiOutdatedFlag) {
      return 'outdated';
    }
  };

  const aiStatusWarningDetails = (
    <div className='flex flex-col gap-0.5'>
      {hasAiPushErrors && (
        <>
          {aiPushErrorsArr.map((errInfo, idx) => (
            <div key={idx} className='flex flex-col gap-0.5'>
              <span>
                {t('candidate-drawer.banner.ats-ai-push-errors', {
                  error: errInfo?.error,
                  interpolation: { escapeValue: false },
                })}
              </span>
              <span>
                {t('candidate-drawer.banner.ats-ai-push-errors-date', {
                  date: formatDate(errInfo?.timestamp, i18n, 'long', true),
                })}
              </span>
            </div>
          ))}
        </>
      )}
      {isAiOutdatedFlag && (
        <span>
          {t('candidate-drawer.banner.ai-instructions-updated-date', {
            date: formatDate(aiInstructionsUpdatedAt, i18n, 'long', true),
          })}
        </span>
      )}
      <span>
        {aiLastSync
          ? t('candidate-drawer.banner.ai-last-sync-date', {
              date: formatDate(aiLastSync, i18n, 'long', true),
            })
          : t('candidate-drawer.banner.not-ai-last-sync')}
      </span>
      {(isAiSyncExternallyMissing || isAiSyncExternallyOutdatedFlag) && (
        <span>
          {aiUpdatedExternallyAtFormatted
            ? t('candidate-drawer.banner.ai-updated-externally', {
                date: aiUpdatedExternallyAtFormatted,
              })
            : t('candidate-drawer.banner.not-ai-updated-externally')}
        </span>
      )}
    </div>
  );

  return {
    showAIStatusWarning,
    aiStatusWarnings,
    aiStatusWarningTooltipContent,
    aiStatusWarningDetails,
    aiStatusWarningIcon,
    aiStatusBannerType,
  };
};
